<template>
  <Section
    id="edit-quote-charges-section"
    :header="t('rateShop.charges.charges', 2)"
    class="edit-quote-charges-section p-0"
    data-class="flex flex-column flex-gap-1"
  >
    <template #headerLeft>
      <CurrencyUnitSelectButton v-model="currencyCode" />
    </template>

    <SkeletonDataTable
      class="edit-quote-charges-datatable"
      :value="chargeItems"
      scrollable
    >
      <Column :header="t('rateShop.charges.charges')">
        <template #body="{ data, index }">
          <span v-if="data.isFreightCharge()">
            <slot name="freightChargeLabel" :charge="data">
              {{ data.chargeCode.name }}
            </slot>
          </span>
          <InputWrapper
            v-else
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            :input-id="data.key"
            :label="t('rateShop.charges.charges')"
            label-class="visually-hidden"
          >
            <RygenDropdown
              :id="inputId"
              :model-value="data.chargeCode"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :options="availableAccessorialsWithExclusions"
              :placeholder="t('referenceData.accessorials')"
              option-label="name"
              data-key="naturalId"
              append-to="body"
              filter
              @update:model-value="
                (accessorial: AccessorialCode) =>
                  onSelectAccessorial(accessorial, data, index)
              "
            />
          </InputWrapper>
        </template>

        <template #footer>
          <span> {{ t('common.paramTotals') }} </span>
        </template>
      </Column>

      <template v-if="canViewUplift">
        <Column :header="t('rateShop.charges.buyRate')">
          <template #body="{ data }">
            <InputWrapper
              v-slot="{ ariaProps, inputClasses, inputId, invalid }"
              v-tooltip.top="{
                value: t('rateShop.charges.selectionRequired'),
                disabled: data.hasChargeSelection,
              }"
              :input-id="data.key"
              :label="t('rateShop.charges.buyRate')"
              label-class="visually-hidden"
            >
              <EditQuoteChargeRateInputGroup
                :id="inputId"
                v-model="data.buyRate"
                v-bind="ariaProps"
                :class="inputClasses"
                :invalid="invalid"
                :currency="currencyCode"
                :min="0"
                :disabled="!data.hasChargeSelection"
                mode="currency"
                @update:model-value="onUpdateBuyRate(data)"
              />
            </InputWrapper>
          </template>

          <template #footer>
            <span> {{ threeplRateTotals.buy }} </span>
          </template>
        </Column>

        <Column>
          <template #header>
            <div class="flex justify-content-between align-items-center w-full">
              <div class="flex flex-gap-1">
                <span
                  class="p-datatable-column-title"
                  data-pc-section="columntitle"
                >
                  {{ t('rateShop.charges.margin') }}
                </span>
                <InfoIcon
                  class="text-gray-darker"
                  position="bottom"
                  :tooltip="t('rateShop.charges.marginEnteredIsUplift')"
                />
              </div>

              <EditQuoteMarginApplyAllPopover
                :charge-items="chargeItems"
                :currency-code="currencyCode"
              />
            </div>
          </template>

          <template #body="{ data }">
            <EditQuoteChargeUpliftRateAndUnitInputGroup
              :id="data.key"
              v-model="data.marginRate"
              v-model:unit="data.upliftUnit"
              class="edit-quote-charges-datatable-column-margin"
              :currency="currencyCode"
              :disabled="!data.hasChargeSelection"
              append-to="body"
              @update:model-value="onUpdateMarginRate(data)"
              @update:unit="onUpdateMarginUnit(data)"
            />
          </template>

          <template #footer>
            <span> {{ threeplRateTotals.margin }} </span>
          </template>
        </Column>

        <Column :header="t('rateShop.charges.sellRate')">
          <template #body="{ data }">
            <InputWrapper
              v-slot="{ ariaProps, inputClasses, inputId, invalid }"
              v-tooltip.top="{
                value: t('rateShop.charges.selectionRequired'),
                disabled: data.hasChargeSelection,
              }"
              :input-id="data.key"
              :label="t('rateShop.charges.sellRate')"
              label-class="visually-hidden"
            >
              <EditQuoteChargeRateInputGroup
                :id="inputId"
                v-model="data.sellRate"
                v-bind="ariaProps"
                :class="inputClasses"
                :invalid="invalid"
                :currency="currencyCode"
                :disabled="!data.hasChargeSelection"
                mode="currency"
                @update:model-value="onUpdateSellRate(data)"
              />
            </InputWrapper>
          </template>

          <template #footer>
            <span> {{ threeplRateTotals.sell }} </span>
          </template>
        </Column>
      </template>
      <Column v-else :header="t('rateShop.rates')">
        <template #body="{ data }">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            v-tooltip.top="{
              value: t('rateShop.charges.selectionRequired'),
              disabled: data.hasChargeSelection,
            }"
            :input-id="data.key"
            :label="t('rateShop.rates', 2)"
            label-class="visually-hidden"
          >
            <EditQuoteChargeRateInputGroup
              :id="inputId"
              v-model="data.rate"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :currency="currencyCode"
              :disabled="!data.hasChargeSelection"
              mode="currency"
            />
          </InputWrapper>
        </template>

        <template #footer>
          <span> {{ clientRateTotal }} </span>
        </template>
      </Column>

      <Column v-if="showStopsColumn" style="max-width: 250px">
        <template #header>
          <span class="p-datatable-column-title" data-pc-section="columntitle">
            {{ t('shipments.stops') }}
          </span>
          <span class="edit-quote-charges-datatable-column-optional-header">
            {{ `(${t('common.optional')})` }}
          </span>
        </template>

        <template #body="{ data }">
          <InputWrapper
            v-if="!data.isFreightCharge()"
            v-slot="{ ariaProps, inputId }"
            v-tooltip.top="{
              value: t('rateShop.charges.selectionRequired'),
              disabled: data.hasChargeSelection,
            }"
            :input-id="`${data.key}-stop`"
            :label="t('shipments.stops')"
            label-class="visually-hidden"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="data.stopSequenceNumber"
              v-bind="ariaProps"
              :options="stops"
              :placeholder="t('shipments.stops')"
              :disabled="!data.hasChargeSelection"
              append-to="body"
              option-value="sequenceNumber"
              filter
              show-clear
            >
              <template #option="{ option }">
                <StopLabel :stop="option" />
              </template>

              <template v-if="!!data.stopSequenceNumber" #value="{ value }">
                <StopLabel :stop="findSelectedStopForSequence(value)" />
              </template>
            </RygenDropdown>
          </InputWrapper>
        </template>
      </Column>

      <Column>
        <template #body="{ data }">
          <InlineRemoveButton
            v-if="!data.isFreightCharge()"
            aria-controls="edit-quote-charges-section"
            @click="onRemoveAccessorial(data)"
          />
        </template>
      </Column>
    </SkeletonDataTable>

    <div>
      <AddButton
        aria-controls="edit-quote-charges-section"
        :label="t('referenceData.accessorials')"
        @click="onAddAccessorialCharge"
      />
    </div>
  </Section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import {
  AccessorialCode,
  AccessorialCodeWellKnownNaturalId,
  Currency,
} from '@rygen/rygen-ui/model';

import {
  BaseEditQuoteChargeTableItem,
  EditQuoteClientChargeTableItem,
  EditQuoteThreeplChargeTableItem,
} from '@/model/rate/editQuote';
import { ParentEntity } from '@/stores/ParentEntity';

import StopLabel from '@/components/location/StopLabel.vue';
import EditQuoteChargeRateInputGroup from '@/components/rateShop/editQuote/EditQuoteChargeRateInputGroup.vue';
import EditQuoteChargeUpliftRateAndUnitInputGroup from '@/components/rateShop/editQuote/EditQuoteChargeUpliftRateAndUnitInputGroup.vue';
import EditQuoteMarginApplyAllPopover from '@/components/rateShop/editQuote/EditQuoteMarginApplyAllPopover.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import CurrencyUnitSelectButton from '@rygen/rygen-ui/components/form/CurrencyUnitSelectButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

const ACCESSORIAL_CODES_TO_EXCLUDE: Set<string> = new Set([
  AccessorialCodeWellKnownNaturalId.LINEHAUL,
  AccessorialCodeWellKnownNaturalId.FUEL,
  AccessorialCodeWellKnownNaturalId.USER_LICENSE,
  AccessorialCodeWellKnownNaturalId.PASSTHROUGH,
  AccessorialCodeWellKnownNaturalId.FUM,
]);

const chargeItems = defineModel({
  type: Array as PropType<BaseEditQuoteChargeTableItem[]>,
  default: () => [],
  required: true,
});

const currencyCode = defineModel('currency-code', {
  type: String as PropType<Currency>,
  default: () => Currency.USD,
});

const props = defineProps({
  accessorialCodes: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
  stops: {
    type: Array as PropType<ParentEntity['stops']>,
    default: () => [],
  },
  canViewUplift: {
    type: Boolean,
    default: () => false,
  },
});
const { accessorialCodes, stops, canViewUplift } = toRefs(props);
defineSlots<{
  freightChargeLabel?: (props: { charge: BaseEditQuoteChargeTableItem }) => any;
}>();

const { t } = useI18n();
const vTooltip = useTooltip();

const threeplRateTotals = computed(() => {
  const currency = { currencyCode: currencyCode.value };

  let buyTotal = 0;
  let marginTotal = 0;
  let sellTotal = 0;

  for (const charge of chargeItems.value) {
    buyTotal += charge.rateAmount('buy');
    marginTotal += charge.rateAmount('margin');
    sellTotal += charge.rateAmount('sell');
  }

  return {
    buy: renderMoney(buyTotal, currency),
    margin: renderMoney(marginTotal, currency),
    sell: renderMoney(sellTotal, currency),
  };
});

const clientRateTotal = computed(() => {
  const total = chargeItems.value
    .map((charge) => charge.rateAmount())
    .reduce((total, curr) => total + curr, 0);

  return renderMoney(total, { currencyCode: currencyCode.value });
});

const availableAccessorialsWithExclusions = computed(
  (): AccessorialCode[] =>
    accessorialCodes.value.filter(
      (accessorial) =>
        !ACCESSORIAL_CODES_TO_EXCLUDE.has(accessorial.naturalId ?? ''),
    ) ?? [],
);

const showStopsColumn = computed(() => {
  return (
    chargeItems.value.filter(
      (accessorial) =>
        !ACCESSORIAL_CODES_TO_EXCLUDE.has(accessorial.chargeNaturalId ?? ''),
    ).length > 0
  );
});

const findSelectedStopForSequence = (stopSequenceNumber: number) => {
  return stops.value.find((stop) => stop.sequenceNumber === stopSequenceNumber);
};

const onRemoveAccessorial = (
  removedChargeItem: BaseEditQuoteChargeTableItem,
) => {
  chargeItems.value = chargeItems.value.filter(
    (item) => item.key !== removedChargeItem.key,
  );
};

const onSelectAccessorial = (
  selectedAccessorial: AccessorialCode,
  chargeItem: BaseEditQuoteChargeTableItem,
  index: number,
) => {
  if (chargeItem.isFreightCharge()) {
    console.error(
      `onSelectAccessorial: Invalid index-${index}, attempted to modify ${chargeItem} instead.`,
    );
    return;
  }

  chargeItem.key = `${index}-${selectedAccessorial.naturalId}`;
  chargeItem.chargeCode = selectedAccessorial;
};

/**
 * 3PL with uplift view only
 *
 * Buy rate cannot be computed, only modified
 * Modifying buy rate will compute a new sell rate
 */
const onUpdateBuyRate = (chargeItem: EditQuoteThreeplChargeTableItem) => {
  chargeItem.sellRate = chargeItem.calculateSellRate();
};

/**
 * 3PL with uplift view only
 *
 * Margin rate can be computed and modified (flat or percent).
 * Modifying margin rate will compute a new sell rate
 * A new margin rate is computed when sell rate is modified
 */
const onUpdateMarginRate = (chargeItem: EditQuoteThreeplChargeTableItem) => {
  chargeItem.sellRate = chargeItem.calculateSellRate();
};

/**
 * 3PL with uplift view only
 *
 * Sell rate can be computed and modified.
 * Modifying sell rate will compute a new margin rate
 * A new sell rate is computed when buy or margin rate is modified
 */
const onUpdateSellRate = (chargeItem: EditQuoteThreeplChargeTableItem) => {
  chargeItem.marginRate = chargeItem.calculateMarginRate();
};

/**
 * 3PL with uplift view only
 */
const onUpdateMarginUnit = (chargeItem: EditQuoteThreeplChargeTableItem) => {
  chargeItem.marginRate = chargeItem.calculateMarginRate();
};

const onAddAccessorialCharge = async () => {
  const key = `${chargeItems.value.length}-accessorial`;

  if (canViewUplift.value) {
    chargeItems.value.push(new EditQuoteThreeplChargeTableItem({ key: key }));
  } else {
    chargeItems.value.push(new EditQuoteClientChargeTableItem({ key: key }));
  }
};
</script>

<style>
.edit-quote-charges-section {
  .edit-quote-charges-datatable {
    .edit-quote-charges-datatable-column-optional-header {
      font-style: italic;
      font-weight: 300;
      text-transform: none;
    }
  }
}
</style>
